<template lang="pug">
  .chats-content-body
    // 一覧
    .chat-messages_container
      .chat-messages(ref="chatMessagesContainer")
        .chat-messages_fetchingPastMessage(v-if="fetchingPastMessage")
          span 読込中...
        .chat-messages_oldestMessage(v-if="isOldestMessage")
          span 最後のメッセージです
        .chat-messages_sentinel(ref="oldestMessageSentinel" v-if="!isOldestMessage")
        .chat-content
          mentorChat(
            v-for='(mentorComment,index) in mentorComments'
            :key='mentorComment.id',
            :mentorComment='mentorComment',
            :currentUser='currentUser',
            :id='"mentor_comment_" + mentorComment.id',
            :isLatest='index === mentorComments.length - 1',
            :broadcastName='broadcastName',
            :filterOptions='filterOptions',
            :selectedFilterValue='selectedFilterValue',
            @clear-filter='handleClearFilter',
            @set-filter='handleSetFilter',
          )
        .chat-messages_sentinel(ref="latestMessageSentinel")
      .chat-messages_container_newMessage(v-if="hasUnreadMessages" @click="scrollToBottom")
        span
          i.fa-solid.fa-arrow-down
        span 新着メッセージがあります
      .chat-messages_container_toBottomButton(v-if="!isAtBottom" @click="scrollToBottom")
        i.fa-solid.fa-arrow-down
    // メッセージ入力フォーム
    .chat-input
      .a-form-tabs.js-tabs
        .a-form-tabs__tab.js-tabs__tab(
          :class='{ "is-active": isActive("mentor-comment") }',
          @click='changeActiveTab("mentor-comment")')
          | コメント
        .a-form-tabs__tab.js-tabs__tab(
          :class='{ "is-active": isActive("preview") }',
          @click='changeActiveTab("preview")')
          | プレビュー
        .a-form-tabs__tab.js-tabs__tab(
          :class='{ "is-active": isActive("form-hidden") }',
          @click='changeActiveTab("form-hidden")')
          | 閉じる
        .chat_filter(v-if="localFilterOptions && localFilterOptions.length > 0")
          .a-button.is-sm.is-secondary.is-select.is-block
            select.js-choices-single-select(v-model="selectedFilterValue" @change="applyFilter")
              // localFilterOptions は、{ label: "グループ名", options: [[表示名, 値], ...] } の配列
              // 外側の v-for で optgroup を生成
              option(value="") 全て
              template(v-for="group in localFilterOptions")
                optgroup(:label="group.label" :key="group.label")
                  // 内側の v-for で各グループ内の option を生成
                  option(v-for="option in group.options" :key="option[1]" :value="option[1]")
                    | {{ option[0] }}
      .a-markdown-input.js-markdown-parent
        .a-markdown-input__inner.js-tabs__content(
          :class='{ "is-active": isActive("mentor-comment") }')
          .form-textarea
            .form-textarea__body
              textarea#js-new-mentor-comment.a-text-input.scrollable.is-fit.js-warning-form.a-markdown-input__textarea(
                v-model='body',
                ref='newMentorComment',
                name='new_mentor_comment[body]',
                data-preview='#new-mentor-comment-preview',
                data-input='.new-mentor-comment-file-input',
                @compositionstart="isComposing = true",
                @compositionend="isComposing = false",
                @keydown.stop='handleKeyDown',
                @input='editMentorComment'
              )
            //- 入力フォームfooter
            .chat-form-footer
              .chat-actions
                ul.chat-actions__items
                  // ファイル添付
                  li.card-main-actions__item.is-sub
                    label.mentor-comment-actions__muted-action.is-de
                      i.fa-solid.fa-paperclip
                      input.new-mentor-comment-file-input(type='file', multiple hidden)
                  // 送信（コメント作成）
                  li.card-main-actions__item.is-sub
                    .mentor-comment-actions__muted-action.is-de(
                      @click='createMentorComment',
                      :class="{ 'is-processing': isCreating }"
                    )
                      i.fa-solid.fa-paper-plane
                  .a-form-help
                    p
                      | Ctrl + Enter で送信
        .a-markdown-input__inner.js-tabs__content(
          :class='{ "is-active": isActive("preview") }')
          #new-mentor-comment-preview.a-long-text.is-md.a-markdown-input__preview.scrollable
</template>
<script>
import CSRF from 'csrf'
import MentorChat from 'mentor-chat.vue'
import TextareaInitializer from 'textarea-initializer'
import role from 'role'
import ActionCable from 'actioncable'
import toast from 'toast'
import { setFavicon, DEFAULT_FAVICON_PATH, UNREAD_FAVICON_PATH } from 'favicon';

export default {
  components: {
    mentorChat: MentorChat,
  },
  mixins: [role, toast],
  props: {
    mentorCommentableType: { type: String, required: true },
    mentorCommentableId: { type: String, required: true },
    currentUserId: { type: Number, required: true },
    currentUser: { type: Object, required: true },
    broadcastName: { type: String, required: true },
    filterOptions: { type: String, required: false },
  },
  data() {
    return {
      mentorComments: [],
      body: '',
      tab: 'mentor-comment',
      isCreating: false,
      defaultTextareaSize: null,
      loading: false,
      fetchingPastMessage: false,
      editing: false,
      isComposing: false,
      topObserver: null,
      bottomObserver: null,
      isAtBottom: false,
      isOldestMessage: false,
      hasUnreadMessages: false,
      localFilterOptions: [],
      selectedMentorCommentableType: '',
      selectedMentorCommentableId: '',
      selectedFilterValue: '', // フィルタ用セレクトボックスの選択値を保持するデータプロパティ
      isChatEnabled: false, // チャット機能の有効・無効を制御するフラグ
      pollingInterval: null,
    }
  },
  computed: {
    validation() {
      return this.body.length > 0
    },
    /**
     * 選択されたフィルター値からモデル名を取得します。
     * 例: "Company,123"  -> "Company"
     * "User,456"    -> "User"
     * "" (空文字列) -> "" (空文字列)
     * @returns {string} モデル名（または空文字列）
     */
    getMentorCommentableType() {
      if (this.filterOptions) {
        const value = this.selectedFilterValue;
        // 値が空文字列やnull/undefinedの場合は、そのまま空文字列を返す（「全て」に対応）
        if (!value) {
          return '';
        }
        // カンマで分割し、最初の部分（モデル名）を取得
        const parts = value.split(',');
        return parts[0] || ''; // 配列が空になる可能性も考慮し、デフォルト値として空文字列を返す
      } else {
        return this.mentorCommentableType;
      }
    },
    /**
     * 選択されたフィルター値からレコードIDを取得します。
     * 例: "Company,123"  -> "123"
     * "User,456"    -> "456"
     * "" (空文字列) -> "" (空文字列)
     * @returns {string} レコードID（文字列として、または空文字列）
     */
    getMentorCommentableId() {
      if (this.filterOptions) {
        const value = this.selectedFilterValue;

        // 値が空文字列やnull/undefinedの場合は、そのまま空文字列を返す（「全て」に対応）
        if (!value) {
          return '';
        }

        // カンマで分割し、2番目の部分（レコードID）を取得
        const parts = value.split(',');
        // parts[1]が存在する場合のみそれを返し、存在しない場合は空文字列を返す
        // （例えば "Company" のようにIDがない形式の入力があった場合などに対応）
        return parts[1] || '';
      } else {
        return this.mentorCommentableId;
      }
    },
  },
  created() { // レンダリング前のデータ取得処理
    // 一覧表示
    this.fetchChats({ mode: 'initial' })
  },
  mounted() { // レンダリング後の描画処理
    // ページロード時に時刻をチェックし、チャット機能を制御
    this.checkChatAndManageConnection();
    // リアルタイムチャットなので、時間チェックを定期的に実行する
    this.pollingInterval = setInterval(() => this.checkChatAndManageConnection(), 60 * 1000); // 60秒ごとに実行

    // this.initializeActionCable();
    this.initializeObservers();
    this.initializeTextarea();
    this.updateBrowserNotification();

    if (this.filterOptions) {
      try {
        // JSON文字列をパースして localFilterOptions に割り当てる
        this.localFilterOptions = JSON.parse(this.filterOptions);
      } catch (e) {
        // console.error("data-filter-options属性のパースに失敗しました:", e, this.filterOptions);
        // エラー発生時はデフォルト値で初期化するなどのハンドリング
        this.localFilterOptions = [];
      }
    }
  },
  beforeUnmount() {
    if (this.chatChannel) this.chatChannel.unsubscribe();
    if (this.consumer) this.consumer.disconnect();
    if (this.topObserver) this.topObserver.disconnect();
    if (this.bottomObserver) this.bottomObserver.disconnect();
    if (this.pollingInterval) clearInterval(this.pollingInterval);
  },
  methods: {
    initializeActionCable() {
      // 接続があれば、一度切断
      if (this.chatChannel) {
        this.chatChannel.unsubscribe();
      }

      const channelName = 'Mentor::ChatChannel'
      const self = this;

      this.consumer = ActionCable.createConsumer(process.env.VUE_APP_ACTION_CABLE_URL);
      this.chatChannel = this.consumer.subscriptions.create(
        {
          channel: channelName,
          mentor_commentable_type: this.getMentorCommentableType,
          mentor_commentable_id: this.getMentorCommentableId,
        }, {
        connected() {
          console.log('講師チャットに接続しました')
          // console.log(`channel: ${channelName} mentor_commentable_type: ${self.getMentorCommentableType} mentor_commentable_id: ${self.getMentorCommentableId} に接続しました`)
        },
        disconnected() {
          console.log('講師チャットの接続が切れました')
          // console.log(`channel: ${channelName} mentor_commentable_type: ${self.getMentorCommentableType} mentor_commentable_id: ${self.getMentorCommentableId} の接続が切れました`)
        },
        received: (data) => {
          if (data.mode == 'create') {
            self.fetchChats(data)
          } else if (data.mode == 'update') {
            self.updateChat(data)
          } else if (data.mode == 'delete') {
            self.deleteChat(data)
          }
        }
      })
    },
    initializeObservers() {
      if (this.topObserver) {
        this.topObserver.disconnect();
        this.topObserver = null; // 参照をクリアして、ガベージコレクションの対象にする
      }
      if (this.bottomObserver) {
        this.bottomObserver.disconnect();
        this.bottomObserver = null; // 参照をクリア
      }

      // メッセージの最上部を監視、見えたら過去メッセージを読み込む
      this.topObserver = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !this.loading && !this.isOldestMessage) {
          this.fetchChats({ mode: 'past' });
        }
      });
      this.topObserver.observe(this.$refs.oldestMessageSentinel);

      // メッセージの最下部を監視、見えたら未読フラグやfavicon通知をオフ
      this.bottomObserver = new IntersectionObserver(([entry]) => {
        this.isAtBottom = entry.isIntersecting;
        if (this.isAtBottom && this.hasUnreadMessages) {
          this.hasUnreadMessages = false;
          this.updateBrowserNotification();
        }
      }, {
        rootMargin: "0px 0px 0px 0px"
      })
      this.bottomObserver.observe(this.$refs.latestMessageSentinel);
    },
    initializeTextarea() {
      this.$nextTick(() => {
        TextareaInitializer.initialize('#js-new-mentor-comment')
        const textarea = document.getElementById('js-new-mentor-comment')
        this.defaultTextareaSize = textarea.scrollHeight
      })
    },
    resizeTextarea() {
      const textarea = document.getElementById('js-new-mentor-comment')
      textarea.style.height = `${this.defaultTextareaSize}px`
    },
    clearForm() {
      this.body = ''
      this.clearPreview('new-mentor-comment-preview')
      this.tab = 'mentor-comment'
      this.isCreating = false
      this.resizeTextarea()
    },
    clearPreview(elementId) {
      const parent = document.getElementById(elementId)
      while (parent.lastChild) {
        parent.removeChild(parent.lastChild)
      }
    },
    editMentorComment() {
      if (this.body.length > 0) {
        this.editing = true
      }
    },
    handleKeyDown(e) {
      if (document.activeElement != e.target) return;

      // Safariでの日本語変換確定時（keyCodeが229）のエンターキー操作を無視
      if (e.keyCode === 229) return;

      if (this.isComposing) return; // 日本語入力中は無視

      // Ctrl + Enter押下は改行する
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        if (!this.isCreating) {
          this.createMentorComment();
        }
        return;
      }
    },
    isActive(tab) {
      return this.tab === tab
    },
    changeActiveTab(tab) {
      this.tab = tab

      // プレビュータブがアクティブになった時だけ実行
      if (tab === 'preview') {
        this.$nextTick(() => {
          Prism.highlightAll()
        })
      }
    },
    getOldestChatId() {
      if (this.mentorComments.length === 0) return 0
      return this.mentorComments[0].id
    },
    getLatestChatId() {
      if (this.mentorComments.length == 0) {
        return ''
      }

      const latestComment = this.mentorComments[this.mentorComments.length - 1];
      return latestComment.id;
    },
    async fetchChats({ mode, user_id }) {
      // 読み込み中、または過去ログをすべて読み込み済みの場合は処理を中断
      if (this.loading || (mode === 'past' && this.isOldestMessage)) {
        return;
      }

      // ローディング状態を開始
      this.loading = true;

      if (mode === 'past' || mode === 'initial') {
        this.fetchingPastMessage = true;
      }

      try {
        // APIリクエストのパラメータを決定
        const isLatestRequest = mode === 'create' || mode === 'initial';
        const chatId = isLatestRequest ? this.getLatestChatId() : this.getOldestChatId();
        const idParam = isLatestRequest ? 'after_id' : 'before_id';
        const endpoint = isLatestRequest ? '/api/mentor/chats/latest.json' : '/api/mentor/chats/past.json';

        // 過去ログ読み込みの場合、DOM更新前のスクロールコンテナの高さを記録
        let oldScrollHeight = 0;
        const chatMessagesContainer = this.$refs.chatMessagesContainer;
        if (mode === 'past' && chatMessagesContainer) {
          oldScrollHeight = chatMessagesContainer.scrollHeight;
        }

        const url = `${endpoint}?${idParam}=${chatId}&mentor_commentable_type=${this.getMentorCommentableType}&mentor_commentable_id=${this.getMentorCommentableId}`

        // APIリクエストを実行
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin',
          redirect: 'manual'
        });

        // APIからのエラーレスポンスをハンドリング
        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        const json = await response.json();
        const comments = json.mentor_comments;

        // 取得したコメントが0件の場合の処理
        if (comments.length === 0) {
          this.isOldestMessage = true;
          return; // finallyブロックは実行される
        }

        // 取得したコメントを配列に追加
        comments.forEach((c) => {
          if (isLatestRequest) {
            this.mentorComments.push(c);
          } else {
            this.mentorComments.unshift(c);
          }
        });

        // DOM更新が完了した後にUI操作を実行
        this.$nextTick(() => {
          if (mode === 'initial') {
            // 初回ロード時は、アニメーションなしで一番下にスクロール
            this.scrollToBottom({ behavior: 'auto' });
          } else if (mode === 'create') {
            // 新規作成時
            if (this.currentUser.id != user_id) {
              // 他のユーザーからのメッセージなら、未読通知を出す
              this.hasUnreadMessages = true;
            } else {
              // 自分の投稿なら、スムーズに一番下にスクロール
              this.scrollToBottom({ behavior: 'smooth' });
            }
          } else if (mode === 'past') {
            // 過去ログ読み込み時
            if (chatMessagesContainer) {
              // スクロール位置を維持する
              const newScrollHeight = chatMessagesContainer.scrollHeight;
              chatMessagesContainer.scrollTop = newScrollHeight - oldScrollHeight;
            }
          }
          // faviconの表示を更新
          this.updateBrowserNotification();

          if (mode === 'create') {
            // 取得したコメントが20件の場合場合、もう一度取得
            if (comments.length === 20) {
              this.fetchChats({ mode: 'create', user_id: user_id })
            }
          }
        });

      } catch (error) {
        // エラー処理
        console.warn(error);
        this.displayToast('メッセージの読み込みに失敗しました。');
      } finally {
        // 後処理
        this.loading = false;
        this.fetchingPastMessage = false;
        this.$nextTick(() => {
          Prism.highlightAll();
        })
      }
    },
    /**
     * mentorComments配列内の特定のチャットデータを更新し、画面を再レンダリングします。
     * @param {object} mentor_comment - APIから返された更新後のチャットオブジェクト。idプロパティを含んでいる必要があります。
     */
    updateChat({ mentor_comment }) {
      // mentorCommentオブジェクトにidがない場合は処理を中断
      if (!mentor_comment || typeof mentor_comment.id === 'undefined') {
        //console.error('更新対象のチャットデータにIDが含まれていません。', mentor_comment);
        return;
      }

      // コメントを探し出し、置き換える
      this.mentorComments = this.mentorComments.map(comment =>
        comment.id === mentor_comment.id ? mentor_comment : comment
      );

      this.$nextTick(() => {
        Prism.highlightAll()
      })
    },
    /**
     * 指定されたIDを持つチャット要素を画面から削除
     * @param {object} payload - ペイロードオブジェクト
     * @param {number|string} payload.mentor_comment_id - 削除するチャットのID
     */
    deleteChat({ mentor_comment_id }) {
      // mentorComments 配列から、指定されたidと一致しないものだけを残す（実質的な削除）
      this.mentorComments = this.mentorComments.filter(mentorComment => mentorComment.id !== mentor_comment_id);
    },
    scrollToBottom({ behavior = 'smooth' } = {}) {
      this.$nextTick(() => {
        const container = this.$refs.chatMessagesContainer;
        if (container) {
          container.scrollTo({ top: container.scrollHeight, behavior: behavior });

          if (this.hasUnreadMessages) {
            this.hasUnreadMessages = false;
            this.updateBrowserNotification(); // ファビコンをデフォルトに戻す
          }
        }
      });
    },
    // メッセージ作成
    async createMentorComment() {
      if (!this.validation) return; // validation computedを使う

      this.isCreating = true;

      try {
        const params = {
          mentor_comment: {
            body: this.body
          },
          mentor_commentable_type: this.getMentorCommentableType || this.mentorCommentableType,
          mentor_commentable_id: this.getMentorCommentableId || this.mentorCommentableId,
        };

        const response = await fetch(`/api/mentor/chats`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-Token': CSRF.getToken()
          },
          body: JSON.stringify(params)
        });

        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        // リアルタイムチャットが無効のとき、チャットをfetchする
        if (!this.isChatEnabled) {
          this.fetchChats({ mode: 'create', user_id: currentUser.id })
        }

        // 成功時、フォームをクリア
        this.clearForm();
      } catch (error) {
        console.error('コメントの作成に失敗しました:', error);
        this.displayToast('コメントの作成に失敗しました。'); // mixinのtoastを呼ぶ

      } finally {
        this.isCreating = false;
      }
    },
    // 未読状態をリセットするメソッド
    resetUnreadState() {
      this.hasUnreadMessages = false;
      this.updateBrowserNotification(); // ブラウザ通知をリセット
    },
    // ブラウザ通知（ファビコン）を更新
    updateBrowserNotification() {
      if (this.hasUnreadMessages) { // 未読メッセージがある場合のみ未読ファビコンに変更
        setFavicon(UNREAD_FAVICON_PATH);
      } else {
        setFavicon(DEFAULT_FAVICON_PATH);
      }
    },
    // チャットフィルタリング
    applyFilter() {
      this.mentorComments = []
      this.isOldestMessage = false
      this.hasUnreadMessages = false
      this.$nextTick(() => {
        this.checkChatAndManageConnection()
        //this.initializeActionCable()
        this.initializeObservers()
        this.fetchChats({ mode: 'initial' })
      })
    },
    /**
    * 子コンポーネントのバッジがクリックされた際に呼び出され、フィルターをクリアします。
    */
    handleClearFilter() {
      this.selectedFilterValue = ''; // フィルター値を空文字に設定
      this.applyFilter(); // フィルターを再適用して表示を更新
    },
    /**
    * 子コンポーネントから特定のフィルター値が設定されるよう通知を受け取ります。
    * @param {string} filterValue - 設定するフィルター値（例: "Company,123"）
    */
    handleSetFilter(filterValue) {
      this.selectedFilterValue = filterValue; // フィルター値を設定
      this.applyFilter(); // フィルターを再適用
    },
    /**
    * チャット有効時間ならばリアルタイムチャットに接続する
    */
    checkChatAndManageConnection() {
      const [startHour, startMinute] = [9, 0];
      const [endHour, endMinute] = [19, 0];
      const now = new Date();

      // 開始と終了の日時オブジェクトを生成
      // 現在の日付を使い、指定された時分を組み合わせて比較
      const startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), startHour, startMinute);
      const endTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), endHour, endMinute);

      // 終了時間が開始時間より前の場合は、翌日として扱う
      if (endTime < startTime) {
        endTime.setDate(endTime.getDate() + 1);
      }

      // 現在時刻が有効期間内にあるかどうかを判定
      const isNowEnabled = (now >= startTime && now < endTime);

      // Action Cable に戻す場合ifを切り替える
      if (this.isChatEnabled !== isNowEnabled) {
        this.isChatEnabled = isNowEnabled;

        if (this.isChatEnabled) {
          this.initializeActionCable();
          console.log('チャットが有効になりました。接続を再開します。');
        } else {
          if (this.chatChannel) {
            this.chatChannel.unsubscribe();
            this.chatChannel = null;
          }
          if (this.consumer) {
            this.consumer.disconnect();
            this.consumer = null;
          }
          console.log('チャットが停止しました。接続を切断します。');
        }
      }

      // 有効な時間内 かつ リアルタイム通信が無効のとき、ポーリング
      if (isNowEnabled && !this.isChatEnabled) {
        // 停止中はチャットをポーリング
        this.fetchChats({ mode: 'create' })
      }
    },
  }
}
</script>
